import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@t-one-internal/design-system';
import { Warning, Clock, CalendarBlank, CaretDown, CaretRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import type { PendingGroup } from '../../hooks/usePendingItemsData';

// --- Helpers ---

const formatDueDate = (dueDate: string): string => {
    const due = new Date(dueDate);
    return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const getDueDateInfo = (dueDate: string | null): { label: string; formattedDate: string; badgeClass: string; icon: React.ReactNode } | null => {
    if (!dueDate) return null;

    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const formattedDate = formatDueDate(dueDate);

    if (diffDays < 0) {
        return {
            label: 'Overdue',
            formattedDate,
            badgeClass: 'bg-red-50 text-red-600 border border-red-200',
            icon: <Warning className="w-3 h-3 shrink-0" weight="bold" />,
        };
    }
    if (diffDays === 0) {
        return {
            label: 'Due Today',
            formattedDate,
            badgeClass: 'bg-amber-50 text-amber-600 border border-amber-200',
            icon: <Clock className="w-3 h-3 shrink-0" weight="bold" />,
        };
    }
    if (diffDays <= 3) {
        return {
            label: 'Due Soon',
            formattedDate,
            badgeClass: 'bg-amber-50 text-amber-600 border border-amber-200',
            icon: <Clock className="w-3 h-3 shrink-0" weight="bold" />,
        };
    }
    if (diffDays <= 7) {
        return {
            label: 'This Week',
            formattedDate,
            badgeClass: 'bg-blue-50 text-blue-600 border border-blue-200',
            icon: <CalendarBlank className="w-3 h-3 shrink-0" weight="bold" />,
        };
    }
    return {
        label: 'Upcoming',
        formattedDate,
        badgeClass: 'bg-slate-50 text-slate-500 border border-slate-200',
        icon: <Clock className="w-3 h-3 shrink-0" />,
    };
};


// --- Types ---

interface PendingItemsWidgetProps {
    data?: PendingGroup[];
    loading?: boolean;
    error?: string;
    onRoleFilterChange?: (role: string) => void;
    onTaskTypeFilterChange?: (taskType: string) => void;
}

// --- Component ---

export const PendingItemsWidget: React.FC<PendingItemsWidgetProps> = ({
    data = [],
    loading,
    error,
    onRoleFilterChange,
    onTaskTypeFilterChange,
}) => {
    const navigate = useNavigate();
    const [roleFilter, setRoleFilter] = useState<string>('');
    const [taskTypeFilter, setTaskTypeFilter] = useState<string>('');
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

    const handleItemClick = useCallback((e: React.MouseEvent, actionUrl?: string) => {
        e.preventDefault();
        if (actionUrl && actionUrl !== '#') {
            navigate(actionUrl);
        }
    }, [navigate]);

    // Persist dropdown options and total count from unfiltered data
    const [allRoleCounts, setAllRoleCounts] = useState<Map<string, number>>(new Map());
    const [allTaskTypeCounts, setAllTaskTypeCounts] = useState<Map<string, number>>(new Map());
    const [unfilteredTotalCount, setUnfilteredTotalCount] = useState<number>(0);

    useEffect(() => {
        if (!roleFilter && !taskTypeFilter && data.length > 0) {
            const rMap = new Map<string, number>();
            const tMap = new Map<string, number>();
            let total = 0;
            data.forEach((g) => {
                total += g.count;
                if (g.assignee_role) rMap.set(g.assignee_role, (rMap.get(g.assignee_role) || 0) + g.count);
                if (g.task_type) tMap.set(g.task_type, (tMap.get(g.task_type) || 0) + g.count);
            });
            setAllRoleCounts(rMap);
            setAllTaskTypeCounts(tMap);
            setUnfilteredTotalCount(total);
        }
    }, [data, roleFilter, taskTypeFilter]);

    const roles = useMemo(() => Array.from(allRoleCounts.keys()).sort(), [allRoleCounts]);
    const taskTypes = useMemo(() => Array.from(allTaskTypeCounts.keys()).sort(), [allTaskTypeCounts]);

    const filteredCount = useMemo(() => data.reduce((sum, g) => sum + g.count, 0), [data]);

    const sortedData = useMemo(
        () =>
            [...data].sort((a, b) => {
                if (!a.due_date && !b.due_date) return 0;
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
            }),
        [data],
    );

    const handleRoleChange = (value: string) => {
        setRoleFilter(value);
        onRoleFilterChange?.(value);
    };

    const handleTaskTypeChange = (value: string) => {
        setTaskTypeFilter(value);
        onTaskTypeFilterChange?.(value);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full py-8 text-red-500 text-sm">
                {error}
            </div>
        );
    }

    const toggleGroup = (key: string) => {
        setExpandedGroup((prev) => (prev === key ? null : key));
    };

    return (
        <div className="p-5 h-full flex flex-col">
            {/* Header with Filters */}
            <div className="flex items-center gap-3 mb-3">
                <h3 className="font-semibold text-gray-900 text-sm">Pending Tasks</h3>
                <span className="min-w-[20px] h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-[11px] font-semibold px-1.5">
                    {filteredCount}
                </span>
                <div className="flex items-center gap-2 ml-auto">
                    <Select value={roleFilter} onValueChange={handleRoleChange}>
                        <SelectTrigger className="h-7 text-xs min-w-[110px]">
                            <SelectValue placeholder="All Roles" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="">All Roles ({unfilteredTotalCount})</SelectItem>
                            {roles.map((role) => (
                                <SelectItem key={role} value={role}>
                                    {role.charAt(0).toUpperCase() + role.slice(1)} ({allRoleCounts.get(role)})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={taskTypeFilter} onValueChange={handleTaskTypeChange}>
                        <SelectTrigger className="h-7 text-xs min-w-[110px]">
                            <SelectValue placeholder="All Types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="">All Types ({unfilteredTotalCount})</SelectItem>
                            {taskTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)} ({allTaskTypeCounts.get(type)})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Task Groups */}
            <div className="flex-1 min-h-0 space-y-2.5 overflow-y-auto">
                {sortedData.length === 0 ? (
                    <div className="text-center py-6 text-gray-500 text-sm">No pending tasks</div>
                ) : (
                    sortedData.map((group) => {
                        const groupKey = `${group.task_type}::${group.title}`;
                        const dueDateInfo = getDueDateInfo(group.due_date);
                        const isExpanded = expandedGroup === groupKey;

                        return (
                            <div key={groupKey}>
                                {/* Group row */}
                                <div
                                    className="flex items-center gap-3 p-[3px] rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                                    onClick={() => toggleGroup(groupKey)}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-gray-800 flex items-center gap-1.5">
                                            {group.title}
                                            {group.count > 1 && (
                                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">
                                                    {group.count}
                                                </span>
                                            )}
                                        </div>
                                        {dueDateInfo && (
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${dueDateInfo.badgeClass}`}>
                                                    {dueDateInfo.icon}
                                                    {dueDateInfo.label}
                                                </span>
                                                <span className="text-gray-300 text-[10px]">&bull;</span>
                                                <span className="text-xs text-gray-400">{dueDateInfo.formattedDate}</span>
                                            </div>
                                        )}
                                    </div>

                                    <CaretDown
                                        className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                        weight="bold"
                                    />
                                </div>

                                {/* Expanded items */}
                                {isExpanded && group.items.length > 0 && (
                                    <div className="ml-5 mt-1 space-y-1">
                                        {group.items.map((item) => (
                                            <div
                                                key={item.id}
                                                onClick={(e) => handleItemClick(e, item.action_url)}
                                                className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 transition-colors group cursor-pointer"
                                            >
                                                {/* Avatar initials */}
                                                {item.subject_first_name && (
                                                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-[10px] font-semibold shrink-0">
                                                        {item.subject_first_name.charAt(0)}
                                                        {item.subject_last_name?.charAt(0) || ''}
                                                    </div>
                                                )}
                                                <span className="text-xs text-gray-700 flex-1 truncate">
                                                    {item.subject_first_name
                                                        ? `${item.subject_first_name} ${item.subject_last_name || ''}`
                                                        : `Item #${item.source_id || item.id}`}
                                                </span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${item.status === 'pending'
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : item.status === 'overdue'
                                                        ? 'bg-red-100 text-red-600'
                                                        : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                                </span>
                                                <CaretRight
                                                    className="w-3.5 h-3.5 text-gray-300 group-hover:text-indigo-500 transition-colors shrink-0"
                                                    weight="bold"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default PendingItemsWidget;
